import { Spacing } from '@persona-id/design-tokens';
import { LinkExternalIcon } from '@persona-id/design-tokens/icons';
import { noop } from 'lodash/fp';
import { FC, useMemo } from 'react';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import Tooltip from 'dashboard/components/Tooltip';
import { WorkflowModuleAttributes } from 'dashboard/models/workflow-module';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import { Label, Span } from 'design-system/components/Text';

export type CallerUIState = {
  id: string;
  selected: boolean;
};

export const Container = styled('div')`
  background: ${({ theme }) => theme.color.variants.caution.disabled};
  padding: ${Spacing[2]};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  width: 100%;
`;

const ReferencedWorkflowItem = styled('div')<{ selectable: boolean }>`
  padding: ${Spacing[3]};
  display: grid;
  grid-template-columns: ${({ selectable }) => (selectable ? '5% 45% 50%' : '50% 50%')};
  background: ${({ theme }) => theme.color.primary.white};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const StyledLabel = styled(Label)<{ selectable: boolean }>`
  ${({ selectable }) => (selectable ? 'cursor: pointer' : '')};
`;

const StyledLink = styled('a')`
  display: flex;
  align-items: center;
`;

const StyledIcon = styled(LinkExternalIcon)`
  margin-left: ${Spacing[1]};
`;

interface WorkflowModuleCallersListProps {
  callers: WorkflowModuleAttributes['callers'];
  callerUIStates?: CallerUIState[];
  latestPublishedBlockId?: string;
  selectable?: boolean;
  onUpdateCallerUIState?: (config: CallerUIState, index: number) => void;
}

const WorkflowModuleCallersList: FC<WorkflowModuleCallersListProps> = ({
  callers,
  callerUIStates,
  latestPublishedBlockId,
  selectable = false,
  onUpdateCallerUIState = noop,
}) => {
  const numSelected = useMemo(
    () => (callerUIStates || []).filter(({ selected }) => selected).length,
    [callerUIStates],
  );
  return (
    <Container>
      <Accordion
        title={
          <StyledLabel
            selectable
            weight='bold'
            color='primary'
          >{`Workflows using this module (${selectable ? `${numSelected}/${callers.length} selected` : callers.length})`}</StyledLabel>
        }
        titleGapSize={Spacing[1]}
      >
        <Spacer height={1} />
        {callers.map(
          (
            {
              'source-id': workflowId,
              'source-name': workflowName,
              'referenced-block-id': referencedBlockId,
            },
            index,
          ) => {
            const selected = callerUIStates?.[index]?.selected || false;
            const disabled = latestPublishedBlockId === referencedBlockId;
            return (
              <ReferencedWorkflowItem key={workflowId} selectable={selectable}>
                {selectable && (
                  <Tooltip
                    trigger='hover'
                    tooltip={disabled ? <Span>Already using the latest module version</Span> : null}
                    hideArrow={true}
                    placement='right'
                  >
                    <Checkbox
                      aria-label={`Toggle selection for ${workflowName}`}
                      checked={selected}
                      disabled={disabled}
                      onChange={(e) => {
                        onUpdateCallerUIState(
                          {
                            id: workflowId,
                            selected: e.target.checked,
                          },
                          index,
                        );
                      }}
                    />
                  </Tooltip>
                )}
                <StyledLabel
                  id='workflow-module-callers-list__name'
                  selectable={selectable && !disabled}
                  onClick={() =>
                    selectable &&
                    !disabled &&
                    onUpdateCallerUIState(
                      {
                        id: workflowId,
                        selected: !selected,
                      },
                      index,
                    )
                  }
                >
                  {workflowName}
                </StyledLabel>
                <StyledLink
                  href={`/dashboard/workflows/${workflowId}`}
                  target='_blank'
                  rel='noreferrer'
                >
                  {workflowId}
                  <StyledIcon />
                </StyledLink>
              </ReferencedWorkflowItem>
            );
          },
        )}
      </Accordion>
    </Container>
  );
};

export default WorkflowModuleCallersList;
